import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import {
    InstructivoEmbarqueStateService,
    WizardState,
} from '../services/instructivo-embarque-state.service';
import { InstructivoEmbarqueService } from '../services/instructivo-embarque.service';
import { Step1ViajeComponent } from './steps/step1-viaje/step1-viaje.component';
import { Step2BookingOrganizacionesComponent } from './steps/step2-booking-organizaciones/step2-booking-organizaciones.component';
import { Step3CargaFinancierosComponent } from './steps/step3-carga-financieros/step3-carga-financieros.component';
import { Step4RevisionComponent } from './steps/step4-revision/step4-revision.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WizardInitParamsDto } from '@opb-workspace/shared';
@Component({
    selector: 'app-wizard',
    standalone: true,
    imports: [
        CommonModule,
        MatStepperModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        // MatSnackBar se usa via servicio inyectado, no como import del componente.
        Step1ViajeComponent,
        Step2BookingOrganizacionesComponent,
        Step3CargaFinancierosComponent,
        Step4RevisionComponent,
    ],
    templateUrl: './wizard.component.html',
    styleUrls: ['./wizard.component.scss'],
})
export class WizardComponent implements OnInit {
    @ViewChild('stepper') stepper!: MatStepper;
    @ViewChild(Step2BookingOrganizacionesComponent)
    step2Component?: Step2BookingOrganizacionesComponent;
    @ViewChild(Step3CargaFinancierosComponent)
    step3Component?: Step3CargaFinancierosComponent;

    state: WizardState | null = null;
    isLoading = false;
    loadError = '';

    constructor(
        public stateService: InstructivoEmbarqueStateService,
        private service: InstructivoEmbarqueService,
        private router: Router,
        private route: ActivatedRoute,
        private snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        this.stateService.state.subscribe((s) => (this.state = s));
        let params = this.stateService.snapshot.initParams;
        if (!params) {
            const fromQuery = this.readInitParamsFromQuery();
            if (fromQuery) {
                this.stateService.initSession(fromQuery);
                params = fromQuery;
            }
        }
        if (!params) {
            this.router.navigate(['/itinerarios']);
            return;
        }
        this.loadVoyageData(params);
    }

    private readInitParamsFromQuery(): WizardInitParamsDto | null {
        const qp = this.route.snapshot.queryParamMap;
        const v = qp.get('voyageId');
        const pol = qp.get('portOfLoadingId');
        const pod = qp.get('portOfDischargeId');
        if (v == null || pol == null || pod == null || v === '' || pol === '' || pod === '') {
            return null;
        }
        const voyageId = Number(v);
        const portOfLoadingId = Number(pol);
        const portOfDischargeId = Number(pod);
        if (
            !Number.isFinite(voyageId) ||
            !Number.isFinite(portOfLoadingId) ||
            !Number.isFinite(portOfDischargeId)
        ) {
            return null;
        }
        return { voyageId, portOfLoadingId, portOfDischargeId };
    }

    private loadVoyageData(params: {
        voyageId: number;
        portOfLoadingId: number;
        portOfDischargeId: number;
    }): void {
        this.isLoading = true;
        this.loadError = '';
        this.service.initWizard(params).subscribe({
            next: (data) => {
                this.stateService.setVoyageData(data);
                this.isLoading = false;
            },
            error: (err: {
                status?: number;
                statusText?: string;
                error?: { message?: string };
            }) => {
                const status = err?.status ?? 'desconocido';
                const detail =
                    err?.error?.message ?? err?.statusText ?? 'Sin detalle';
                this.loadError =
                    `No se pudo cargar la información del viaje ${params.voyageId}. ` +
                    `Backend respondió: ${status} — ${detail}`;
                this.isLoading = false;
                // eslint-disable-next-line no-console
                console.error('[WizardComponent] initWizard error:', err);
            },
        });
    }

    get currentStep(): number {
        return this.state?.currentStep ?? 0;
    }

    next(): void {
        const currentIdx = this.stepper.selectedIndex;
        // Validar el paso actual antes de avanzar.
        // Steps: 0=Viaje (read-only), 1=Booking+Organizaciones, 2=Carga+Financieros, 3=Revisión.
        if (currentIdx === 1) {
            if (this.step2Component?.isBookingEmpty) {
                this.snackBar.open(
                    'Debe ingresar o seleccionar un booking antes de continuar.',
                    'Cerrar',
                    { duration: 4000, panelClass: 'snackbar-warning' }
                );
                return;
            }
            const f = this.step2Component?.form;
            if (f && f.invalid) {
                f.markAllAsTouched();
                this.snackBar.open(
                    'Complete los campos obligatorios antes de avanzar (exportador, consignatario y notificante).',
                    'Cerrar',
                    { duration: 4000, panelClass: 'snackbar-warning' }
                );
                return;
            }
            const availCount = this.step2Component?.availableContainerItems.length ?? 0;
            const selCount = this.step2Component?.selectedContainerIds.size ?? 0;
            if (availCount > 0 && selCount === 0) {
                this.snackBar.open(
                    'Seleccione al menos un contenedor para continuar.',
                    'Cerrar',
                    { duration: 4000, panelClass: 'snackbar-warning' }
                );
                return;
            }
            this.step2Component?.syncFormToWizardState();
        }
        if (currentIdx === 2) {
            // feat-ie-wizard-mejoras (punto 5): dejar de bloquear el avance cuando quedan
            // contenedores sin asignar — solo se avisa. Los contenedores no asignados
            // simplemente no forman parte de ningún instructivo en el envío (CA-05).
            // El texto es deliberadamente informativo, no imperativo: "antes de continuar"
            // daba la sensación de bloqueo aunque el avance ya no se detiene (feedback cliente
            // 2026-08). El contenedor sigue disponible para un instructivo futuro.
            const remaining = this.step3Component?.remainingContainerCount ?? 0;
            if (remaining > 0) {
                this.snackBar.open(
                    `Quedan ${remaining} contenedor${remaining === 1 ? '' : 'es'} sin asignar a ningún instructivo. Puede continuar; los podrá asignar en otro instructivo más adelante.`,
                    'Cerrar',
                    { duration: 5000, panelClass: 'snackbar-warning' }
                );
            }
            const f = this.step3Component?.form;
            const instrFormsValid = this.step3Component?.isInstructivoFormsValid() ?? true;
            if ((f && f.invalid) || !instrFormsValid) {
                f?.markAllAsTouched();
                this.step3Component?.markAllInstructivoFormsTouched();
                this.snackBar.open(
                    'Complete los campos obligatorios de carga (ítems), especies (Especie y FOB unitario) y antecedentes financieros antes de avanzar.',
                    'Cerrar',
                    { duration: 5000, panelClass: 'snackbar-warning' }
                );
                return;
            }
        }
        this.stepper.next();
        this.stateService.setStep(this.stepper.selectedIndex);
    }

    previous(): void {
        this.stepper.previous();
        this.stateService.setStep(this.stepper.selectedIndex);
    }

    cancel(): void {
        if (this.shouldConfirmCancel()) {
            if (
                !confirm(
                    '¿Desea cancelar el instructivo? Los cambios no guardados se perderán.'
                )
            ) {
                return;
            }
        }
        this.stateService.reset();
        this.goBackToItinerarios();
    }

    /**
     * Sólo pedir confirmación si el usuario YA empezó a llenar el wizard:
     * - Aplicó un booking (bookingData !== null), o
     * - Avanzó más allá del paso inicial (currentStep > 0).
     * Si recién entró y todavía no hizo nada, volver sin preguntar.
     */
    private shouldConfirmCancel(): boolean {
        const s = this.state;
        if (!s) return false;
        if (s.bookingData) return true;
        if ((s.currentStep ?? 0) > 0) return true;
        return false;
    }

    /**
     * Vuelve a Itinerarios preservando los query params (filtros) del usuario
     * via `history.back()`. Si no hay history (entró directo al wizard), usa
     * navigate como fallback.
     */
    private goBackToItinerarios(): void {
        if (typeof window !== 'undefined' && window.history.length > 1) {
            window.history.back();
            return;
        }
        this.router.navigate(['/itinerarios']);
    }
}
